import { ExternalLink, Gauge } from 'lucide-react';
import { DashPanel } from './DashPanel';
import { useDailyUsage } from '../hooks/useDailyUsage';
import type { DailyUsageBucket } from '../data/dailyUsage';

function barColor(ratio: number): string {
  if (ratio >= 0.9) return 'bg-destructive';
  if (ratio >= 0.7) return 'bg-amber-500';
  return 'bg-primary';
}

function UsageBar({ label, bucket }: { label: string; bucket: DailyUsageBucket }) {
  const pct = Math.min(100, Math.round(bucket.ratio * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-foreground font-medium">{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {bucket.used.toLocaleString('pt-BR')} / {bucket.cap.toLocaleString('pt-BR')} ({pct}%)
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${barColor(bucket.ratio)}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Painel "Cota diária do Firestore" — lê `/api/usage/today` (mesmos contadores fatiados que
 * `firestore.rules` já usa pra travar gravação/exclusão, ver conectar.md). Só gravação e
 * exclusão têm cap próprio (ver comentário em firestore.rules sobre por que leitura não tem);
 * leitura é acompanhada pelo painel de uso do próprio Firebase Console, sem custo de cota.
 */
export function QuotaUsagePanel() {
  const { usage, loading } = useDailyUsage();

  if (loading || !usage) return null;

  return (
    <DashPanel padding="md">
      <div className="flex items-center gap-2 mb-4">
        <Gauge className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Cota diária do Firestore (plano Spark)</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
        <UsageBar label="Gravações hoje" bucket={usage.writes} />
        <UsageBar label="Exclusões hoje" bucket={usage.deletes} />
      </div>
      <a
        href="https://console.firebase.google.com/project/conectar-a1ecd/firestore"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        Leituras não têm teto próprio — acompanhe na aba "Uso" do Firebase Console
        <ExternalLink className="h-3 w-3" />
      </a>
    </DashPanel>
  );
}
